import { h, mount } from "@/lib/dom";
import { loading, emptyState, button, badge, toast, confirmDialog, editModeButton } from "@/components/ui";
import { icon } from "@/lib/icons";
import { navigate } from "@/app/router";
import { subjectsService, topicsService, pbErrorMessage, isSonstigesSubject } from "@/services";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { checkConnectivity, connectivityMessage } from "@/services/offline/connectivity";
import { pickCurrentTopic, openTopics } from "@/services/topics.service";
import { openSubjectForm } from "@/features/subjects/subjectForm";
import { openSettings } from "@/features/settings/settingsModal";
import { openTopicForm } from "@/features/topics/topicForm";
import { chooseTopic } from "@/features/topics/topicPicker";
import { maybeShowOnboarding, openOnboarding } from "@/features/onboarding";
import { defaultColor, contrastText } from "@/lib/colors";
import { subjectIcon } from "@/lib/subjectIcons";
import { daysUntil } from "@/lib/format";
import { isCollapsed, setCollapsed, isOpen, setOpen } from "@/lib/settings";
import type { Subject, Topic, TopicState } from "@/types/models";

const STATE_RANK: Record<TopicState, number> = {
  active: 0,
  done: 1,
  archived: 2,
};
const STATE_LABEL: Record<TopicState, { label: string; kind: string }> = {
  active: { label: "Aktiv", kind: "active" },
  done: { label: "Erledigt", kind: "done" },
  archived: { label: "Archiv", kind: "archived" },
};
const collapseKey = (subjectId: string) => `subject:${subjectId}`;
const archiveKey = (subjectId: string) => `archive:${subjectId}`;

/** Hauptseite: Fächer als Boxen; Themen sortiert, Box ein-/ausklappbar. */
export function HomePage(): HTMLElement {
  const container = h("div", null);
  let initialized = false;
  // Zuletzt geladener Stand – erlaubt sofortiges Neuzeichnen (z. B. beim
  // Umschalten des Bearbeitungsmodus) ohne erneute Serverabfrage.
  let lastSubjects: Subject[] = [];
  let lastTopics = new Map<string, Topic[]>();
  // Bearbeitungsmodus gilt nur für diesen Seitenbesuch – kein globaler,
  // persistenter Zustand. Beim Verlassen der Seite verschwindet die Closure
  // und der Modus ist automatisch wieder aus.
  let editing = false;

  function grouped(subjects: Subject[], topics: Topic[]): Map<string, Topic[]> {
    const map = new Map<string, Topic[]>();
    for (const s of subjects) map.set(s.id, []);
    for (const t of topics) map.get(t.subject)?.push(t);
    for (const list of map.values()) list.sort((a, b) => a.created.localeCompare(b.created));
    return map;
  }

  async function load() {
    let fromCache = false;
    if (!initialized) {
      const [cachedSubjects, cachedTopics] = await Promise.all([
        offlineSync.loadSubjects().catch(() => null),
        offlineSync.loadTopics().catch(() => null),
      ]);
      const real = cachedSubjects?.filter((s) => !isSonstigesSubject(s));
      if (real?.length && navigator.onLine) {
        lastSubjects = real;
        lastTopics = grouped(real, cachedTopics ?? []);
        render(real, lastTopics);
        fromCache = true;
      } else mount(container, loading());
    }
    try {
      // Ein Request für alle Themen statt einer je Fach – bei zehn Fächern
      // waren das elf Rundreisen zum Server, jetzt sind es zwei.
      const [subjects, allTopics] = await Promise.all([
        subjectsService.listReal(),
        topicsService.listAll(),
      ]);
      const map = new Map<string, Topic[]>();
      for (const s of subjects) map.set(s.id, []);
      for (const t of allTopics) map.get(t.subject)?.push(t);
      // `listBySubject` sortiert aufsteigend nach Anlagedatum – gleiche
      // Reihenfolge hier, damit die Seite unverändert aussieht.
      for (const list of map.values()) list.sort((a, b) => a.created.localeCompare(b.created));
      initialized = true;
      lastSubjects = subjects;
      lastTopics = map;
      render(subjects, map);
      // Erster Besuch ohne Fächer: Tour + Fächer-Schnellstart anbieten
      maybeShowOnboarding({ hasSubjects: subjects.length > 0, onChanged: refresh });
    } catch (err) {
      initialized = true;
      if (fromCache) return;
      const [cachedSubjectsRaw, cachedTopics] = await Promise.all([offlineSync.loadSubjects(), offlineSync.loadTopics()]);
      const cachedSubjects = cachedSubjectsRaw?.filter((s) => !isSonstigesSubject(s));
      if (cachedSubjects) {
        const map = new Map<string, Topic[]>();
        for (const s of cachedSubjects) map.set(s.id, []);
        for (const t of cachedTopics ?? []) map.get(t.subject)?.push(t);
        for (const list of map.values()) list.sort((a, b) => a.created.localeCompare(b.created));
        lastSubjects = cachedSubjects;
        lastTopics = map;
        render(cachedSubjects, map);
        toast("Offline – zeige zwischengespeicherten Stand.", "warning");
        return;
      }
      const status = await checkConnectivity();
      mount(
        container,
        h(
          "div",
          { class: "card card-pad" },
          emptyState({
            icon: "folder",
            title: "Konnte nicht laden",
            text: status.backendReachable ? pbErrorMessage(err) : connectivityMessage(status),
            action: button({ label: "Erneut versuchen", onClick: () => void load() }),
          })
        )
      );
    }
  }
  const refresh = () => void load();

  /**
   * Wendet Ein-/Ausklappen direkt auf vorhandene DOM-Knoten an – animiert,
   * kein Rebuild. Der Sammel-Chevron oben zieht dabei mit, damit sein Pfeil
   * auch beim Bearbeiten-Umschalten dieselbe Drehung macht wie beim
   * eigenen Klick.
   */
  function applyCollapsedToAll(collapsed: boolean): void {
    for (const s of lastSubjects) setCollapsed(collapseKey(s.id), collapsed);
    container.querySelectorAll(".subject-box").forEach((b) => b.classList.toggle("collapsed", collapsed));
    const all = container.querySelector<HTMLButtonElement>(".collapse-all");
    if (all) {
      all.classList.toggle("is-collapsed", collapsed);
      all.title = collapsed ? "Alle Fächer aufklappen" : "Alle Fächer zuklappen";
    }
  }

  function render(subjects: Subject[], map: Map<string, Topic[]>) {
    const allCollapsed =
      subjects.length > 0 && subjects.every((s) => isCollapsed(collapseKey(s.id)));

    const collapseBtn = button({
      icon: "chevron-down",
      variant: "ghost",
      title: allCollapsed ? "Alle Fächer aufklappen" : "Alle Fächer zuklappen",
      onClick: (e) => {
        const next = !subjects.every((s) => isCollapsed(collapseKey(s.id)));
        applyCollapsedToAll(next);
        const btn = e.currentTarget as HTMLButtonElement;
        btn.classList.toggle("is-collapsed", next);
        btn.title = next ? "Alle Fächer aufklappen" : "Alle Fächer zuklappen";
      },
    });
    collapseBtn.classList.add("collapse-all");
    collapseBtn.classList.toggle("is-collapsed", allCollapsed);

    const editBtn = editModeButton({
      active: editing,
      onToggle: () => {
        const willEdit = !editing;
        editBtn.classList.toggle("active", willEdit);
        editBtn.title = willEdit ? "Bearbeiten beenden" : "Bearbeiten";
        // Erst zuklappen, dann im zugeklappten Zustand neu aufbauen. Beim
        // AKTIVIEREN geht es danach wieder auf (eigene Ausklapp-Animation
        // statt Sprung) – beim DEAKTIVIEREN bleibt es zu: sonst hätte das
        // Zuklappen keinen sichtbaren Effekt, sobald man mit dem Bearbeiten
        // fertig ist, und alles klappt sofort wieder von selbst auf.
        applyCollapsedToAll(true);
        window.setTimeout(() => {
          editing = willEdit;
          render(lastSubjects, lastTopics);
          if (willEdit) requestAnimationFrame(() => requestAnimationFrame(() => applyCollapsedToAll(false)));
        }, 300);
      },
    });

    const header = h(
      "div",
      { class: "page-header" },
      h("h1", null, "Meine Fächer"),
      h("span", { class: "spacer" }),
      subjects.length > 0 ? collapseBtn : null,
      editBtn,
      button({
        icon: "settings",
        variant: "ghost",
        title: "Fächer verwalten",
        onClick: () => openSettings({ subjects, onChanged: refresh }),
      })
    );

    // Nach Kategorien gruppieren (Reihenfolge = position; Überschrift bei Wechsel).
    const grouped: HTMLElement[] = [];
    let lastCat: string | null = null;
    let grid: HTMLElement | null = null;
    for (const s of subjects) {
      const cat = s.category ?? "";
      if (grid === null || (cat && cat !== lastCat)) {
        if (cat && cat !== lastCat) {
          grouped.push(h("h2", { class: "subject-category" }, cat));
        }
        grid = h("div", { class: "subjects-grid" });
        grouped.push(grid);
      }
      if (cat) lastCat = cat;
      grid.appendChild(subjectBox(s, map.get(s.id) ?? [], editing));
    }

    const body =
      subjects.length === 0
        ? h(
            "div",
            { class: "card card-pad" },
            emptyState({
              icon: "folder",
              title: "Noch keine Fächer",
              text: "Wähle deine Fächer aus der Vorlage – oder lege selbst eines an.",
              action: h(
                "div",
                { class: "row", style: "gap:8px;justify-content:center;flex-wrap:wrap" },
                button({
                  label: "Fächer auswählen (Schnellstart)",
                  icon: "check",
                  variant: "primary",
                  onClick: () => openOnboarding(refresh),
                }),
                button({
                  label: "Eigenes Fach anlegen",
                  icon: "plus",
                  onClick: () => openSubjectForm({ onSaved: refresh }),
                })
              ),
            })
          )
        : h("div", null, ...grouped);

    mount(container, h("div", null, header, body));
  }

  function subjectBox(subject: Subject, topics: Topic[], editing: boolean): HTMLElement {
    const accent = subject.color || defaultColor();
    // Status automatisch aus Klausurterminen ableiten.
    const states = computeStates(topics);
    const all = topics
      .map((t) => ({ ...t, state: states.get(t.id) ?? t.state }))
      .sort(compareTopics);
    // Archivierte Themen ausblenden – sie sind im Bearbeitungsmodus aufklappbar.
    const sorted = all.filter((t) => t.state !== "archived");
    const archived = all.filter((t) => t.state === "archived");
    const current = pickCurrentTopic(sorted, subject.mainTopic);
    // Ein Hauptthema zu wählen lohnt erst, wenn mehrere Themen gleichwertig
    // nebeneinander laufen.
    const choosable = openTopics(sorted);
    const canPickMain = choosable.length > 1;

    const head = h(
      "div",
      { class: "subject-head" },
      h("span", {
        class: "subject-icon",
        style: `background:${accent};color:${contrastText(accent)}`,
        html: subjectIcon(subject.icon, 18),
      }),
      h(
        "button",
        {
          class: "subject-name",
          title: current ? "Zum Thema" : "Thema hinzufügen",
          onclick: () => void openSubjectTopic(subject, sorted, current),
        },
        subject.title
      )
    );

    // Archiv: nur im Bearbeitungsmodus sichtbar, Zustand bleibt gespeichert.
    let archiveBlock: HTMLElement | null = null;
    if (editing && archived.length) {
      const aKey = archiveKey(subject.id);
      // Standard ist zugeklappt: `isCollapsed` liefert für unbekannte
      // Schlüssel false, sonst spränge beim ersten Bearbeiten jedes Archiv auf.
      // Gespeichert wird deshalb der geöffnete Zustand.
      const open = isOpen(aKey);
      const list = h(
        "div",
        { class: "topic-list archive-list" },
        ...archived.map((t) => topicRow(t, editing, subject, false))
      );
      const wrap = h("div", { class: "archive-wrap" }, list);
      const toggle = h(
        "button",
        {
          class: "subject-toggle archive-toggle",
          "aria-expanded": String(open),
          onclick: () => {
            const nowOpen = block.classList.toggle("open");
            setOpen(aKey, nowOpen);
            toggle.setAttribute("aria-expanded", String(nowOpen));
          },
        },
        h("span", { class: "toggle-chev", html: icon("chevron-down", 14) }),
        `Archiv (${archived.length})`
      );
      const block = h("div", { class: "archive-block" + (open ? " open" : "") }, toggle, wrap);
      archiveBlock = block;
    }

    // Thema anlegen geht außerhalb des Bearbeitungsmodus über den Fachnamen
    // (siehe oben) – die Zeile hier ist nur im Bearbeitungsmodus vorhanden.
    const subjectBody = h(
      "div",
      { class: "subject-body" },
      sorted.length
        ? h(
            "div",
            { class: "topic-list" },
            ...sorted.map((t) => topicRow(t, editing, subject, canPickMain))
          )
        : h("div", { class: "subject-empty" }, "Noch keine Themen."),
      archiveBlock,
      editing
        ? h(
            "div",
            { class: "add-row" },
            button({
              label: "Thema hinzufügen",
              icon: "plus",
              variant: "ghost",
              size: "sm",
              block: true,
              onClick: () => openTopicForm({ subjectId: subject.id, onSaved: refresh }),
            })
          )
        : null
    );

    // Standard: zugeklappt. Nutzer kann pro Sitzung aufklappen, nach Navigation
    // oder Reload startet die Startseite immer wieder zugeklappt.
    const collapsed = isCollapsed(collapseKey(subject.id), true);
    const box = h("div", {
      class: "card subject-box" + (collapsed ? " collapsed" : ""),
      style: `--accent:${accent}`,
    });
    const chevron = h(
      "button",
      {
        class: "subject-chevron",
        title: "Ein-/Ausklappen",
        "aria-expanded": String(!collapsed),
      },
      h("span", { class: "row", html: icon("chevron-down", 18) })
    );
    chevron.onclick = () => {
      const now = box.classList.toggle("collapsed");
      setCollapsed(collapseKey(subject.id), now);
      chevron.setAttribute("aria-expanded", String(!now));
    };
    head.appendChild(chevron);

    box.appendChild(head);
    // Der Wrapper animiert die Höhe (grid-template-rows 1fr -> 0fr).
    box.appendChild(h("div", { class: "subject-body-wrap" }, subjectBody));
    return box;
  }

  function topicRow(
    topic: Topic,
    editing: boolean,
    subject: Subject,
    canPickMain: boolean
  ): HTMLElement {
    const isMain = subject.mainTopic === topic.id;
    const title = h(
      "button",
      {
        class: "t-title",
        title: topic.title,
        onclick: () => navigate(`/topic/${topic.id}`),
      },
      topic.title
    );
    const pickMain =
      canPickMain && topic.state === "active"
        ? mainTopicBtn(topic, subject, isMain)
        : null;
    const controls = editing
      ? h(
          "span",
          { class: "row", style: "gap:4px" },
          pickMain,
          archiveTopicBtn(topic),
          deleteTopicBtn(topic)
        )
      : stateBadge(topic.state);
    return h(
      "div",
      {
        class:
          "topic-row" +
          (topic.state === "archived" ? " topic-archived" : "") +
          (isMain ? " is-main" : ""),
      },
      title,
      controls
    );
  }

  /**
   * Klick auf den Fachnamen: laufen mehrere Themen nebeneinander, fragt der
   * Wähler nach (Hauptthema vorausgewählt) – sonst geht es direkt hinein.
   */
  async function openSubjectTopic(
    subject: Subject,
    topics: Topic[],
    current: Topic | undefined
  ): Promise<void> {
    if (!current) {
      openTopicForm({ subjectId: subject.id, onSaved: refresh });
      return;
    }
    const topic = await chooseTopic({ subject, topics });
    if (topic) navigate(`/topic/${topic.id}`);
  }

  /** Stern: macht das Thema zum Hauptthema des Fachs (nochmal klicken hebt es auf). */
  function mainTopicBtn(topic: Topic, subject: Subject, isMain: boolean): HTMLElement {
    const btn = button({
      icon: "star",
      variant: "ghost",
      size: "sm",
      title: isMain ? "Ist das Hauptthema – klicken hebt es auf" : "Zum Hauptthema machen",
      onClick: () => void setMainTopic(subject, isMain ? "" : topic.id),
    });
    btn.classList.add("main-topic-btn");
    btn.classList.toggle("active", isMain);
    return btn;
  }

  async function setMainTopic(subject: Subject, topicId: string): Promise<void> {
    try {
      await subjectsService.update(subject.id, { mainTopic: topicId });
      subject.mainTopic = topicId;
      toast(topicId ? "Hauptthema gesetzt." : "Hauptthema aufgehoben.", "success");
      render(lastSubjects, lastTopics);
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Archivieren/Wiederherstellen – im Bearbeitungsmodus die einzige Statusänderung. */
  function archiveTopicBtn(topic: Topic): HTMLElement {
    const archived = topic.state === "archived";
    return button({
      icon: "archive",
      variant: "ghost",
      size: "sm",
      title: archived ? "Wiederherstellen" : "Archivieren",
      onClick: () => void toggleArchive(topic),
    });
  }

  async function toggleArchive(topic: Topic) {
    const archived = topic.state === "archived";
    try {
      await topicsService.update(topic.id, { state: archived ? "active" : "archived" });
      toast(archived ? `„${topic.title}" wiederhergestellt.` : `„${topic.title}" archiviert.`, "success");
      refresh();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Löschen direkt aus der Übersicht – nur im Bearbeitungsmodus sichtbar. */
  function deleteTopicBtn(topic: Topic): HTMLElement {
    return button({
      icon: "trash",
      variant: "ghost",
      size: "sm",
      title: "Thema löschen",
      onClick: () => void removeTopic(topic),
    });
  }

  async function removeTopic(topic: Topic) {
    const ok = await confirmDialog({
      title: "Thema löschen?",
      message: `„${topic.title}" samt Mitschrift, Lernzetteln und Materialien wird gelöscht.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await topicsService.remove(topic.id);
      toast("Thema gelöscht.", "success");
      refresh();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  void load();
  return container;
}

/**
 * Leitet den Status je Thema aus dem Klausurtermin ab:
 * - Termin vorbei -> abgeschlossen
 * - nächster (frühester) zukünftiger Termin im Fach -> aktiv
 * - weitere zukünftige -> anstehend
 * - kein Termin -> gespeicherter Status; archiviert bleibt archiviert.
 */
function computeStates(topics: Topic[]): Map<string, TopicState> {
  const map = new Map<string, TopicState>();
  for (const t of topics) {
    let st: TopicState;
    if (t.state === "archived") st = "archived";
    else if (t.examDate) {
      const du = daysUntil(t.examDate);
      st = du !== null && du < 0 ? "done" : "active";
    } else st = t.state;
    map.set(t.id, st);
  }
  return map;
}

function compareTopics(a: Topic, b: Topic): number {
  const ra = STATE_RANK[a.state] ?? 1;
  const rb = STATE_RANK[b.state] ?? 1;
  if (ra !== rb) return ra - rb;
  if (a.examDate && b.examDate) {
    return new Date(a.examDate).getTime() - new Date(b.examDate).getTime();
  }
  if (a.examDate) return -1;
  if (b.examDate) return 1;
  return 0;
}

function stateBadge(state: TopicState): HTMLElement {
  const s = STATE_LABEL[state] ?? STATE_LABEL.active;
  return badge(s.label, s.kind);
}
